import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import { toast } from 'react-toastify';

import { API_URL } from '../reusable/url';

import './login.css';

import { setUserDetails } from '../reducers/user';

const Login = () => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [token, setToken] = useState('');
    const [error, setError] = useState('');
    const [is2FARequired, setIs2FARequired] = useState(false);
    const [sessionToken, setSessionToken] = useState('');

    const dispatch = useDispatch();
    const navigate = useNavigate();

    const handleLogin = (e) => {
        e.preventDefault();
        setError('');

        const options = {
            method: 'POST',
            headers: { 
                'Content-Type': 'application/json',
            }, 
            body: JSON.stringify({ username, password }),
            credentials: 'include',
        };

        fetch(API_URL('login'), options)
            .then(async res => {
                console.log('Response:', res);

                const data = await res.json().catch(() => ({}));

                if (!res.ok) {
                    if (res.status === 401) {
                        throw new Error('Invalid username or password.');
                    }

                    if (res.status === 503 || res.status === 500) {
                        throw new Error(
                            data.message || 'Login service is temporarily unavailable. Please try again later.'
                        );
                    }

                    throw new Error(data.message || 'Login failed. Please try again.');
                }

                return data;
            })
            .then(data => {
                console.log('Data:', data);
                if (data.message === '2FA required') {
                    setSessionToken(data.token); // Store the session token temporarily
                    setIs2FARequired(true);
                    // Pass sessionToken to Combined2FA component
                    navigate('/enable-2fa', { state: { sessionToken: data.token } });
                } else {
                    dispatch(setUserDetails({
                        username: data.username,
                        role: data.role,
                        token: data.token
                    }));
                    sessionStorage.setItem('token', data.token);
                    sessionStorage.setItem('role', data.role); 
                    navigate('/');
                }
            })
            .catch(err => {
                setError(err.message);
            });
    };

    const handleVerify2FA = (e) => {
        e.preventDefault();
        setError('');

        const options = {
            method: 'POST',
            headers: { 
                'Content-Type': 'application/json', 
                'Authorization': `Bearer ${sessionToken}`,
            },
            body: JSON.stringify({ token }),
            credentials: 'include',
        };

        fetch(API_URL('verify-2fa', 'auth'), options)
            .then(res => {
                console.log('2FA Response:', res);
                if (!res.ok) {
                    throw new Error(res.statusText);
                }
                return res.json();
            })
            .then(data => {
                dispatch(setUserDetails({
                    username: data.username,
                    role: data.role,
                    token: data.token
                }));
                sessionStorage.setItem('token', data.token);
                sessionStorage.setItem('role', data.role); 
                toast.success('2FA verification successful!');
                navigate('/');
            })
            .catch(err => {
                setError('2FA verification error: ' + err.message);
            });
    };

    return (
        <div className="login-template">
            <h1 className="login-title">P-score app</h1>
            {is2FARequired ? (
                <form onSubmit={handleVerify2FA}>
                    <Box sx={{ '& > :not(style)': { m: 1, width: '25ch' } }}>
                        <TextField 
                            required
                            id="outlined-2fa-token"
                            label="2FA Token"
                            type="text"
                            value={token}
                            onChange={(e) => setToken(e.target.value)}
                            color="secondary"
                            focused
                            InputLabelProps={{ style: { color: 'white' } }}
                            sx={{
                                input: { color: 'navy' },
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': { borderColor: 'violet' },
                                    '&:hover fieldset': { borderColor: 'violet' },
                                    '&.Mui-focused fieldset': { borderColor: 'violet' },
                                },
                            }}
                        />
                    </Box>
                    <button type="submit" className="btn-login">Verify 2FA</button>
                </form>
            ) : (
                <form onSubmit={handleLogin}>
                    <Box sx={{ '& > :not(style)': { m: 1, width: '25ch' } }}>
                        <TextField 
                            required
                            id="outlined-required-username"
                            label="Username"
                            type="text"
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            color="secondary"
                            focused
                            InputLabelProps={{ style: { color: 'darkviolet' } }}
                            sx={{
                                input: { color: 'navy' },
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': { borderColor: 'darkviolet' },
                                    '&:hover fieldset': { borderColor: 'darkviolet' },
                                    '&.Mui-focused fieldset': { borderColor: 'darkviolet' },
                                },
                            }}
                        />
                        <TextField 
                            required
                            id="outlined-password-input"
                            label="Password"
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            autoComplete="current-password"
                            color="secondary"
                            focused
                            InputLabelProps={{ style: { color: 'darkviolet' } }}
                            sx={{
                                input: { color: 'navy' },
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': { borderColor: 'darkviolet' },
                                    '&:hover fieldset': { borderColor: 'darkviolet' },
                                    '&.Mui-focused fieldset': { borderColor: 'darkviolet' },
                                },
                            }}
                        />
                    </Box>
                    <div className='login-btns'>
                        <button type="submit" className="btn-login">Login</button>
                        <button type="button" onClick={() => navigate('/register')} className="btn-register">Register</button>
                    </div>
                </form>
            )}
            {error && <div className="errors">{error}</div>}
        </div>
    );
};

export default Login;
